import { CalendarCultivationHierarchyListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/calendar-cultivation-hierarchy-list.response';
import { MonthActions, monthActions } from '../@commons/@services/front/period-bars.util';

/**
 * Le calendrier « ce mois-ci » (PC-11 b) : ce qui se fait dans le mois choisi,
 * en A-Z. Rangées par geste du mois, les lignes changeaient de place à chaque
 * mois touché (retours 3) : les filtres font déjà ce tri.
 *
 * Le 17/10, 2 lignes sur 33 concernaient le mois, sans que l'écran le dise :
 * les autres restent à un toucher, sur l'axe des mois.
 */
export type CalendarFilter = 'all' | 'sow' | 'plant' | 'harvest';

export const CALENDAR_FILTERS: CalendarFilter[] = ['all', 'sow', 'plant', 'harvest'];

/** Au-dessus, un mois est « chargé » : c'est lui qu'on montre depuis un mois creux. */
export const BUSY_MONTH_MIN = 10;
/** Un mois qui n'a pas plus d'un semis est creux. */
const QUIET_MONTH_MAX_SOW = 1;

const nameOf = (hierarchy: CalendarCultivationHierarchyListResponse): string =>
  String(hierarchy.cultivations?.length === 1 ? hierarchy.cultivations[0].name : hierarchy.name ?? '');

const isSow = (actions: MonthActions): boolean => actions.sowSheltered || actions.sow;

function matches(actions: MonthActions, filter: CalendarFilter): boolean {
  switch (filter) {
    case 'sow':
      return isSow(actions);
    case 'plant':
      return actions.plant;
    case 'harvest':
      return actions.harvest;
    default:
      return isSow(actions) || actions.plant || actions.harvest;
  }
}

const byName = (lang: string) => (a: CalendarCultivationHierarchyListResponse, b: CalendarCultivationHierarchyListResponse): number =>
  nameOf(a).localeCompare(nameOf(b), lang.toLowerCase());

/** Les lignes du mois, filtrées, en A-Z : une culture garde sa place d'un mois à l'autre. */
export function rowsForMonth(
  hierarchies: CalendarCultivationHierarchyListResponse[],
  month: number,
  filter: CalendarFilter,
  lang = 'fr',
): CalendarCultivationHierarchyListResponse[] {
  return (hierarchies ?? [])
    .filter((hierarchy) => matches(monthActions(hierarchy.periods as any, month), filter))
    .sort(byName(lang));
}

/** Une recherche montre toutes les cultures qui répondent, en A-Z : le mois ne filtre plus. */
export function rowsForSearch(
  hierarchies: CalendarCultivationHierarchyListResponse[],
  lang = 'fr',
): CalendarCultivationHierarchyListResponse[] {
  return [...(hierarchies ?? [])].sort(byName(lang));
}

/**
 * Depuis un mois creux (« Peu de semis en décembre »), le prochain mois
 * chargé et son nombre de plantes à semer ou planter. Null si le mois n'est
 * pas creux, ou si aucun mois de l'année ne l'est assez.
 */
export function nextBusyMonth(
  hierarchies: CalendarCultivationHierarchyListResponse[],
  month: number,
): { month: number; count: number } | null {
  const sowing = (hierarchies ?? []).filter((hierarchy) => isSow(monthActions(hierarchy.periods as any, month))).length;
  if (sowing > QUIET_MONTH_MAX_SOW) return null;
  for (let step = 1; step < 12; step++) {
    const candidate = ((month - 1 + step) % 12) + 1;
    const count = (hierarchies ?? []).filter((hierarchy) => {
      const actions = monthActions(hierarchy.periods as any, candidate);
      return isSow(actions) || actions.plant;
    }).length;
    if (count >= BUSY_MONTH_MIN) return { month: candidate, count };
  }
  return null;
}
